import React from "react";
import { Box, Grid, Typography, Paper, Chip } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import WaterIcon from "@mui/icons-material/Water";

function ProductsSection() {
  // All brand logos for the scrolling banner
  const brandLogos = [
    { name: "TEXMO", image: "/texmoico.png" },
    { name: "CRI", image: "/cri logo.png" },
    { name: "Crompton", image: "/crompton-greaves-logo.webp" },
    { name: "Mangalam", image: "/mangalam.png" },
    { name: "Finolex", image: null },
    { name: "MeecoCab", image: null },
    { name: "KSB", image: null },
    { name: "Motor Guard", image: null },
    { name: "Southern Switchgears (SSG)", image: null },
    { name: "Surya", image: null },
  ];

  const products = [
    {
      name: "Submersible Pumps",
      brand: "TEXMO, CRI, Crompton",
      icon: <WaterIcon />,
      color: "#ef4444",
      description: "High-performance submersible pumps from leading manufacturers - India's most reliable pump manufacturers with decades of expertise",
    },
    {
      name: "Electrical Cables",
      brand: "Finolex, MeecoCab, KSB",
      icon: <ElectricalServicesIcon />,
      color: "#f59e0b",
      description: "Premium quality wires and cables - India's leading electrical cable manufacturers",
    },
    {
      name: "Control Panels",
      brand: "Motor Guard, SSG",
      icon: <ElectricalServicesIcon />,
      color: "#3b82f6",
      description: "Advanced motor starters and control panels for industrial and residential applications",
    },
    {
      name: "Steel Pipes & Tubes",
      brand: "Surya",
      icon: <LocalShippingIcon />,
      color: "#8b5cf6",
      description: "High-quality steel tubes and pipes for construction and industrial applications",
    },
    {
      name: "HDPE Pipes",
      brand: "Mangalam",
      icon: <LocalShippingIcon />,
      color: "#06b6d4",
      description: "Durable HDPE pipes for borewell, irrigation, water management and effluent discharge",
    },
  ];

  return (
    <Box>
      {/* Scrolling Brands Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          py: 4,
          px: 2,
          overflow: "hidden",
          position: "relative",
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          marginRight: "calc(-50vw + 50%)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(90deg, #0f172a 0%, transparent 20%, transparent 80%, #0f172a 100%)",
            pointerEvents: "none",
            zIndex: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 4 },
            animation: "scroll 30s linear infinite",
            "@keyframes scroll": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-50%)" },
            },
            width: "max-content",
          }}
        >
          {[...brandLogos, ...brandLogos].map((brand, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: { xs: "120px", sm: "150px" },
                height: { xs: "60px", sm: "80px" },
                flexShrink: 0,
              }}
            >
              {brand.image ? (
                <Box
                  component="img"
                  src={brand.image}
                  alt={brand.name}
                  sx={{
                    maxHeight: "70px",
                    objectFit: "contain",
                    filter: "brightness(1.1)",
                    height: { xs: "50px", sm: "70px" },
                    maxWidth: { xs: "100px", sm: "140px" },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: { xs: "0.65rem", sm: "0.9rem" },
                    fontWeight: "700",
                    color: "#e0e7ff",
                    textAlign: "center",
                    px: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {brand.name}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Products Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          py: 8,
          px: 2,
        }}
      >
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: "#0f172a",
                mb: 2,
                fontSize: { xs: "1.8rem", sm: "2rem" },
              }}
            >
              Our Products
            </Typography>
            <Box
              sx={{
                width: "60px",
                height: "4px",
                background: "linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)",
                borderRadius: "2px",
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {products.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 30px ${product.color}25`,
                      border: `1px solid ${product.color}40`,
                    },
                  }}
                >
                  {/* Brand Logos/Names */}
                  <Box
                    sx={{
                      width: "80px",
                      height: "80px",
                      background: `linear-gradient(135deg, ${product.color}20, ${product.color}05)`,
                      border: `2px solid ${product.color}40`,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      fontSize: "2rem",
                      color: product.color,
                    }}
                  >
                    {product.icon}
                  </Box>

                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{
                      color: "#0f172a",
                      mb: 0.5,
                      fontSize: "1.1rem",
                    }}
                  >
                    {product.name}
                  </Typography>

                    <Chip
                      label={product.brand}
                      size="small"
                      sx={{
                        background: `${product.color}20`,
                        color: product.color,
                        fontWeight: "600",
                        mb: 2,
                        width: "fit-content",
                        borderRadius: "8px",
                      }}
                    />

                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      color: "#64748b",
                      lineHeight: "1.6",
                    }}
                  >
                    {product.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default ProductsSection;
